/**
 * Generic Adapter
 * Platform-agnostic fallback using DOM-based detection
 */

import { BaseAdapter } from './base-adapter';
import { ProductData, CartData, OrderData } from '../types';

export class GenericAdapter extends BaseAdapter {
  
  private addToCartButtonSelectors = [
    'button[name="add-to-cart"]',
    'button.add-to-cart',
    'button[type="submit"][name*="cart"]',
    'input[type="submit"][name*="cart"]',
    '.add-to-cart-button',
    '[data-product-add]',
    'form[action*="cart"] button[type="submit"]'
  ];

  getName(): string {
    return 'Generic';
  }

  detect(): boolean {
    // Always returns true - this is the fallback adapter
    return true;
  }

  init(): void {
    this.log('Initializing Generic adapter (fallback mode)');
  }

  hookAddToCart(callback: (product: ProductData) => void): void {
    // Monitor clicks on common add-to-cart buttons
    this.addToCartButtonSelectors.forEach(selector => {
      document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        
        if (target.matches(selector) || target.closest(selector)) {
          this.log('Add to cart button clicked');
          
          const productData = this.extractProductFromContext(target);
          if (productData) {
            callback(productData);
          }
        }
      }, true);
    });

    // Monitor form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target as HTMLFormElement;
      
      if (
        form.action?.includes('cart') ||
        form.querySelector('[name*="add"][name*="cart"]')
      ) {
        this.log('Cart form submitted');
        
        const productData = this.extractProductFromForm(form);
        if (productData) {
          callback(productData);
        }
      }
    }, true);

    this.log('Add to cart hooks installed');
  }

  hookProductView(callback: (product: ProductData) => void): void {
    // Detect product pages via URL patterns (including Croatian/German variants)
    const isProductPage = 
      /\/product[s]?\//.test(window.location.pathname) ||
      /\/item[s]?\//.test(window.location.pathname) ||
      /\/produkt\//.test(window.location.pathname) || // German
      /\/proizvod\//.test(window.location.pathname) || // Croatian
      document.querySelector('[itemtype*="schema.org/Product"]') !== null;

    if (isProductPage) {
      this.log('Product page detected');
      
      const productData = this.extractProductFromPage();
      if (productData) {
        callback(productData);
      }
    }
  }

  hookViewCart(callback: (cart?: CartData) => void): void {
    // Detect cart pages via URL patterns
    const isCartPage = 
      /\/cart/.test(window.location.pathname) ||
      /\/basket/.test(window.location.pathname) ||
      /\/bag/.test(window.location.pathname);

    if (isCartPage) {
      this.log('Cart page detected');
      callback(); // Generic adapter doesn't extract cart data reliably
    }
  }

  hookCheckout(callback: (cart?: CartData) => void): void {
    // Detect checkout pages via URL patterns
    const isCheckoutPage = 
      /\/checkout/.test(window.location.pathname) ||
      /\/payment/.test(window.location.pathname);

    if (isCheckoutPage) {
      this.log('Checkout page detected');
      callback(); // Generic adapter doesn't extract cart data reliably
    }
  }

  hookPurchase(callback: (order: OrderData) => void): void {
    // Detect order confirmation pages
    const isOrderPage = 
      /\/order/.test(window.location.pathname) ||
      /\/confirmation/.test(window.location.pathname) ||
      /\/thank-you/.test(window.location.pathname) ||
      /\/success/.test(window.location.pathname) ||
      document.querySelector('[class*="order-confirm"]') !== null;

    if (isOrderPage) {
      this.log('Order confirmation page detected');
      
      const orderData = this.extractOrderFromPage();
      if (orderData) {
        callback(orderData);
      }
    }
  }

  async getCart(): Promise<CartData | null> {
    // Generic adapter cannot reliably fetch cart data
    // Return null to indicate cart data is unavailable
    return null;
  }

  /**
   * Extract product data from page context
   */
  private extractProductFromPage(): ProductData | null {
    try {
      // Try schema.org structured data first
      const schemaElement = document.querySelector('script[type="application/ld+json"]');
      if (schemaElement) {
        const schema = JSON.parse(schemaElement.textContent || '{}');
        if (schema['@type'] === 'Product') {
          return {
            productId: schema.sku || schema.productID || '',
            productSku: schema.sku || '',
            productName: schema.name || '',
            productPrice: parseFloat(schema.offers?.price || '0'),
            productUrl: window.location.href
          };
        }
      }

      // Try meta tags
      const ogTitle = document.querySelector('meta[property="og:title"]')?.getAttribute('content');
      const ogPrice = document.querySelector('meta[property="product:price:amount"]')?.getAttribute('content');

      if (ogTitle) {
        return {
          productId: '',
          productName: ogTitle,
          productPrice: ogPrice ? parseFloat(ogPrice) : 0,
          productUrl: window.location.href
        };
      }

      // Fallback: common DOM patterns
      const productName = 
        document.querySelector('.product-name, .product-title, .product h1, [itemprop="name"]')?.textContent?.trim() ||
        document.querySelector('h1')?.textContent?.trim() || '';

      const priceElement = document.querySelector(
        '.product-price, .price, [itemprop="price"], [data-price]'
      );
      const productPrice = priceElement ? 
        parseFloat(priceElement.textContent?.replace(/[^0-9.]/g, '') || '0') : 0;

      return {
        productId: '',
        productName,
        productPrice,
        productUrl: window.location.href
      };
    } catch (error) {
      this.log('Failed to extract product from page', error);
      return null;
    }
  }

  /**
   * Extract product data from clicked button context
   */
  private extractProductFromContext(button: HTMLElement): ProductData | null {
    try {
      // Try to find product container
      const productContainer = button.closest('[data-product-id], [data-product], .product, .product-item');
      
      if (!productContainer) {
        // Fallback to page-level extraction
        return this.extractProductFromPage();
      }

      const productId = 
        productContainer.getAttribute('data-product-id') ||
        productContainer.getAttribute('data-id') || '';

      const productName = 
        productContainer.querySelector('.product-name, .product-title, h2, h3')?.textContent?.trim() || '';

      const priceElement = productContainer.querySelector('.price, .product-price');
      const productPrice = priceElement ? 
        parseFloat(priceElement.textContent?.replace(/[^0-9.]/g, '') || '0') : 0;

      const quantityInput = productContainer.querySelector('input[name*="quantity"]') as HTMLInputElement;
      const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

      return {
        productId,
        productName,
        productPrice,
        quantity
      };
    } catch (error) {
      this.log('Failed to extract product from context', error);
      return null;
    }
  }

  /**
   * Extract product data from form
   */
  private extractProductFromForm(form: HTMLFormElement): ProductData | null {
    try {
      const formData = new FormData(form);
      
      const productId = 
        formData.get('product_id')?.toString() ||
        formData.get('productId')?.toString() ||
        formData.get('id')?.toString() || '';

      const productName = 
        formData.get('product_name')?.toString() ||
        formData.get('name')?.toString() ||
        form.querySelector('[name*="product"][name*="name"]')?.textContent?.trim() || '';

      const quantity = parseInt(formData.get('quantity')?.toString() || '1');

      // Try to get price from page context
      const productPrice = this.extractProductFromPage()?.productPrice || 0;

      return {
        productId,
        productName,
        productPrice,
        quantity
      };
    } catch (error) {
      this.log('Failed to extract product from form', error);
      return null;
    }
  }

  /**
   * Extract order data from confirmation page
   */
  private extractOrderFromPage(): OrderData | null {
    try {
      // Look for order number
      const orderNumberElement = document.querySelector(
        '[class*="order-number"], [class*="order-id"], [data-order-number]'
      );
      const orderNumber = orderNumberElement?.textContent?.replace(/[^0-9]/g, '') || '';

      // Look for total
      const totalElement = document.querySelector(
        '[class*="order-total"], [class*="total"], [data-total]'
      );
      const orderValue = totalElement ? 
        parseFloat(totalElement.textContent?.replace(/[^0-9.]/g, '') || '0') : 0;

      if (orderNumber && orderValue > 0) {
        return {
          orderId: orderNumber,
          orderNumber,
          orderValue,
          currency: 'USD', // Default, cannot reliably detect
          items: []
        };
      }

      return null;
    } catch (error) {
      this.log('Failed to extract order from page', error);
      return null;
    }
  }
}
